/**
 * Historical Performance Analytics Page - Pro Only
 * Comprehensive trading performance analysis dashboard
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, BarChart3, Trophy, Target,
  Calendar, ArrowUpRight, ArrowDownRight, Crown, Lock,
  RefreshCw, Loader2, DollarSign, Percent, Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/analytics/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 403) {
        setError('pro_required');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        toast.error('Failed to load analytics');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Pro upgrade prompt for non-Pro users
  if (error === 'pro_required' || (user && !user.is_pro)) {
    return (
      <div className="min-h-screen bg-slate-950 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="bg-gradient-to-br from-purple-900/30 to-slate-900/50 border-purple-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-purple-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Pro Feature</h1>
              <p className="text-slate-400 mb-6">
                Historical Performance Analytics is available exclusively for Pro members.
                Track your portfolio over time, compare seasons, and benchmark against other traders.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6 text-left">
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <TrendingUp className="h-5 w-5 text-emerald-400 mb-2" />
                  <p className="text-white text-sm font-medium">Portfolio History</p>
                  <p className="text-slate-400 text-xs">Track value over time</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Trophy className="h-5 w-5 text-yellow-400 mb-2" />
                  <p className="text-white text-sm font-medium">Season Comparison</p>
                  <p className="text-slate-400 text-xs">Compare your seasons</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Target className="h-5 w-5 text-blue-400 mb-2" />
                  <p className="text-white text-sm font-medium">Best/Worst Picks</p>
                  <p className="text-slate-400 text-xs">Analyze your trades</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <BarChart3 className="h-5 w-5 text-purple-400 mb-2" />
                  <p className="text-white text-sm font-medium">Benchmark</p>
                  <p className="text-slate-400 text-xs">Compare vs others</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/subscription')}
                className="bg-purple-600 hover:bg-purple-500"
              >
                <Crown className="h-4 w-4 mr-2" />
                Upgrade to Pro
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  const portfolioHistory = analytics?.portfolio_history || {};
  const benchmark = analytics?.benchmark_comparison || {};
  const bestWorst = analytics?.best_worst_picks || {};
  const scoringTrends = analytics?.scoring_trends || [];
  const seasons = analytics?.season_performance || [];

  return (
    <div className="min-h-screen bg-slate-950 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <BarChart3 className="h-7 w-7 text-purple-500" />
              Performance Analytics
              <Badge className="bg-purple-500/20 text-purple-400">PRO</Badge>
            </h1>
            <p className="text-slate-400 mt-1">Track your trading performance over time</p>
          </div>
          <Button onClick={fetchAnalytics} variant="outline" className="border-slate-700">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Portfolio Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Portfolio Value</p>
                  <p className="text-2xl font-bold text-white">
                    ${portfolioHistory.current_value?.toLocaleString() || '100,000'}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-emerald-500/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Return</p>
                  <p className={`text-2xl font-bold ${portfolioHistory.total_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {portfolioHistory.total_return >= 0 ? '+' : ''}${portfolioHistory.total_return?.toLocaleString() || '0'}
                  </p>
                </div>
                {portfolioHistory.total_return >= 0 ? (
                  <ArrowUpRight className="h-8 w-8 text-emerald-500/50" />
                ) : (
                  <ArrowDownRight className="h-8 w-8 text-red-500/50" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Return %</p>
                  <p className={`text-2xl font-bold ${portfolioHistory.total_return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {portfolioHistory.total_return_pct >= 0 ? '+' : ''}{portfolioHistory.total_return_pct?.toFixed(2) || '0'}%
                  </p>
                </div>
                <Percent className="h-8 w-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Your Rank</p>
                  <p className="text-2xl font-bold text-yellow-400">
                    #{benchmark.user_rank || '-'}
                  </p>
                  {benchmark.percentile && (
                    <p className="text-xs text-slate-400">Top {(100 - benchmark.percentile).toFixed(0)}%</p>
                  )}
                </div>
                <Trophy className="h-8 w-8 text-yellow-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Benchmark Comparison */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-400" />
                Benchmark vs Bots
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Your Points</span>
                  <span className="text-white font-bold">{benchmark.user_points || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Bot Average</span>
                  <span className="text-slate-300">{benchmark.bot_avg_points || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Bot Best</span>
                  <span className="text-slate-300">{benchmark.bot_max_points || 0}</span>
                </div>
                <div className="border-t border-slate-700 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">vs Bot Average</span>
                    <span className={`font-bold ${benchmark.vs_bot_avg >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {benchmark.vs_bot_avg >= 0 ? '+' : ''}{benchmark.vs_bot_avg || 0} pts
                      ({benchmark.vs_bot_avg_pct >= 0 ? '+' : ''}{benchmark.vs_bot_avg_pct || 0}%)
                    </span>
                  </div>
                </div>
                <div className="text-center text-xs text-slate-500">
                  {benchmark.total_participants || 0} total participants this season
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scoring Trends */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-400" />
                Weekly Scoring Trends
              </CardTitle>
            </CardHeader>
            <CardContent>
              {scoringTrends.length > 0 ? (
                <div className="space-y-2">
                  {scoringTrends.slice(-6).map((week, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <span className="text-slate-400 text-xs w-20">{week.week}</span>
                      <div className="flex-1 bg-slate-800 rounded-full h-4 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"
                          style={{ width: `${Math.min(100, (week.total_points / 500) * 100)}%` }}
                        />
                      </div>
                      <span className="text-white font-medium w-16 text-right">{week.total_points} pts</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-4">No scoring data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Best Picks */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <ArrowUpRight className="h-5 w-5 text-emerald-400" />
                Best Performers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bestWorst.best_picks?.length > 0 ? (
                <div className="space-y-3">
                  {bestWorst.best_picks.map((pick, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{pick.symbol}</p>
                        <p className="text-slate-400 text-xs">{pick.name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-bold">+{pick.pnl_pct?.toFixed(2)}%</p>
                        <p className="text-emerald-400/70 text-xs">+${pick.pnl?.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-4">No holdings to analyze</p>
              )}
            </CardContent>
          </Card>

          {/* Worst Picks */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <ArrowDownRight className="h-5 w-5 text-red-400" />
                Worst Performers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bestWorst.worst_picks?.length > 0 ? (
                <div className="space-y-3">
                  {bestWorst.worst_picks.map((pick, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{pick.symbol}</p>
                        <p className="text-slate-400 text-xs">{pick.name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-red-400 font-bold">{pick.pnl_pct?.toFixed(2)}%</p>
                        <p className="text-red-400/70 text-xs">${pick.pnl?.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-4">No holdings to analyze</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Season History */}
        {seasons.length > 0 && (
          <Card className="bg-slate-900/50 border-slate-800 mt-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-400" />
                Season History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-slate-400 text-sm font-medium">Season</th>
                      <th className="text-right py-2 px-3 text-slate-400 text-sm font-medium">Points</th>
                      <th className="text-right py-2 px-3 text-slate-400 text-sm font-medium">Rank</th>
                      <th className="text-right py-2 px-3 text-slate-400 text-sm font-medium">Return</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seasons.map((season, idx) => (
                      <tr key={idx} className="border-b border-slate-700/50">
                        <td className="py-2 px-3">
                          <span className="text-white">{season.season}</span>
                          {season.is_current && (
                            <Badge className="ml-2 bg-emerald-500/20 text-emerald-400 text-xs">Current</Badge>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right text-white">{season.total_points}</td>
                        <td className="py-2 px-3 text-right text-yellow-400">#{season.rank || '-'}</td>
                        <td className={`py-2 px-3 text-right ${season.return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {season.return_pct >= 0 ? '+' : ''}{season.return_pct?.toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
